"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Pencil,
  Plus,
  X,
  Rocket,
  RefreshCw,
  ListChecks,
  GraduationCap,
  Search,
  Trash2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgInput } from "@/components/fg-input";
import { FgDialog } from "@/components/fg-dialog";
import { FgToggle } from "@/components/fg-toggle";
import { FgToast } from "@/components/fg-toast";
import { api } from "@/lib/api";

/* ────────────────────────────────────────────────────────────────────────── */

interface Level {
  id: string;
  name: string;
  displayName: string;
}

type ExamType = "mock_exam" | "after_n_lessons";

interface Exam {
  id: string;
  levelId: string;
  type: ExamType;
  title: string;
  description: string | null;
  nLessons: number;
  orderIndex: number;
  isActive: boolean;
}

interface ExamForm {
  levelId: string;
  type: ExamType;
  title: string;
  description: string;
  nLessons: number;
  orderIndex: number;
  isActive: boolean;
}

const TYPE_META: Record<ExamType, { label: string; hint: string; tone: string }> = {
  mock_exam: {
    label: "Mock exam",
    hint: "A full-level mock exam fired from the level detail page.",
    tone: "bg-[#FFD60A]/15 text-[#FFD60A] border-[#FFD60A]/30",
  },
  after_n_lessons: {
    label: "After N lessons",
    hint: "Fired automatically when a learner completes N lessons in this level.",
    tone: "bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/30",
  },
};

export function ExamsPage() {
  const qc = useQueryClient();
  const levels = useQuery<Level[]>({
    queryKey: ["levels"],
    queryFn: async () => (await api.get("/api/levels")).data,
  });
  const [levelId, setLevelId] = useState<string>("");
  const [editing, setEditing] = useState<Exam | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<ExamType | "all">("all");
  const [deleting, setDeleting] = useState<Exam | null>(null);

  const deleteExam = useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(`/api/admin/exams/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["exams", levelId] });
      FgToast.success("Exam deleted");
      setDeleting(null);
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Delete failed"),
  });

  useEffect(() => {
    if (!levelId && levels.data && levels.data.length > 0) {
      setLevelId(levels.data[0].id);
    }
  }, [levels.data, levelId]);

  const exams = useQuery<Exam[]>({
    queryKey: ["exams", levelId],
    enabled: !!levelId,
    queryFn: async () => (await api.get(`/api/levels/${levelId}/exams`)).data,
  });

  const filtered = useMemo(() => {
    const list = exams.data ?? [];
    return list.filter((e) => {
      if (typeFilter !== "all" && e.type !== typeFilter) return false;
      if (search && !e.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [exams.data, typeFilter, search]);

  return (
    <AdminShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[rgba(255,255,255,0.08)] pb-6 mb-8"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <GraduationCap className="w-5 h-5 text-[#FFD60A]" />
            <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#FFD60A]">
              GOETHE / TELC EXAMS
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#FAFAFA]">
            Exams
          </h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            Mock exams and after-N-lessons milestones per level. Wire to
            exercise banks in the exercise admin.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FgButton
            size="sm"
            variant="secondary"
            onClick={() => exams.refetch()}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Refresh
          </FgButton>
          <FgButton
            size="sm"
            variant="primary"
            onClick={() => setCreating(true)}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            New exam
          </FgButton>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr,auto,auto,auto] mb-4">
        <div className="relative">
          <Search
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title…"
            className="w-full h-9 rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] pl-7 pr-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
          />
        </div>
        <select
          value={levelId}
          onChange={(e) => setLevelId(e.target.value)}
          className="h-9 rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-xs text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
        >
          {levels.data?.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} · {l.displayName}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ExamType | "all")}
          className="h-9 rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-xs text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
        >
          <option value="all">All types</option>
          <option value="mock_exam">Mock exam</option>
          <option value="after_n_lessons">After N</option>
        </select>
        <FgButton
          size="sm"
          variant="secondary"
          onClick={() => setCreating(true)}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Add
        </FgButton>
      </div>

      {exams.isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-white/5 fg-skeleton" />
          ))}
        </div>
      )}

      {!exams.isLoading && filtered.length === 0 && (
        <FgCard className="p-10 text-center">
          <ListChecks className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">No exams yet</p>
          <p className="mt-1 text-xs text-[#8E8E93]">
            Add a mock exam or wire an after-N-lessons milestone.
          </p>
        </FgCard>
      )}

      {!exams.isLoading && filtered.length > 0 && (
        <div className="space-y-2">
          <AnimatePresence>
            {filtered.map((exam, idx) => (
              <motion.div
                key={exam.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, delay: idx * 0.04, ease: [0.16, 1, 0.3, 1] }}
              >
                <ExamRow exam={exam} onEdit={setEditing} onDelete={setDeleting} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {(creating || editing) && (
        <ExamEditor
          exam={editing}
          levelId={levelId}
          levels={levels.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["exams", levelId] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {deleting && (
        <FgDialog
          open={!!deleting}
          onClose={() => setDeleting(null)}
          tone="danger"
          title="Delete exam?"
          description={`"${deleting.title}" will be removed permanently. Learners who have it in progress will lose access.`}
          footer={
            <>
              <FgButton variant="ghost" onClick={() => setDeleting(null)}>
                Cancel
              </FgButton>
              <FgButton
                variant="destructive"
                onClick={() => deleteExam.mutate(deleting.id)}
                loading={deleteExam.isPending}
                leftIcon={<Trash2 size={14} />}
              >
                Delete exam
              </FgButton>
            </>
          }
        />
      )}
    </AdminShell>
  );
}

function ExamRow({
  exam,
  onEdit,
  onDelete,
}: {
  exam: Exam;
  onEdit: (e: Exam) => void;
  onDelete: (e: Exam) => void;
}) {
  const meta = TYPE_META[exam.type];
  return (
    <FgCard className="p-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={clsx(
            "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
            meta.tone,
          )}
        >
          {meta.label}
        </span>
        <span className="font-mono text-[10px] bg-white/5 text-[#8E8E93] px-1.5 py-0.5 rounded uppercase">
          #{exam.orderIndex}
        </span>
        <h3 className="text-base font-bold text-[#FAFAFA] flex-1 truncate min-w-0">
          {exam.title}
        </h3>
        {exam.nLessons > 0 && exam.type === "after_n_lessons" && (
          <span className="text-[10px] text-[#8E8E93] font-mono">
            after {exam.nLessons} lessons
          </span>
        )}
        {!exam.isActive && (
          <span className="font-mono text-[10px] bg-[#8E8E93]/15 text-[#8E8E93] border border-[#8E8E93]/30 px-1.5 py-0.5 rounded uppercase">
            Inactive
          </span>
        )}
        <FgButton
          size="sm"
          variant="ghost"
          onClick={() => FgToast.info("Launch flow opens a learner attempt — wire from the mobile admin to fire this")}
          leftIcon={<Rocket size={12} />}
        >
          Launch
        </FgButton>
        <FgButton
          size="sm"
          variant="outline"
          onClick={() => onEdit(exam)}
          leftIcon={<Pencil size={12} />}
        >
          Edit
        </FgButton>
        <FgButton
          size="sm"
          variant="ghost"
          onClick={() => onDelete(exam)}
          leftIcon={<Trash2 size={12} />}
        >
          Delete
        </FgButton>
      </div>
      {exam.description && (
        <p className="mt-2 text-xs text-[#8E8E93]">{exam.description}</p>
      )}
    </FgCard>
  );
}

function ExamEditor({
  exam,
  levelId,
  levels,
  onClose,
  onSaved,
}: {
  exam: Exam | null;
  levelId: string;
  levels: Level[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ExamForm>({
    levelId: exam?.levelId ?? levelId,
    type: exam?.type ?? "mock_exam",
    title: exam?.title ?? "Mock exam",
    description: exam?.description ?? "",
    nLessons: exam?.nLessons ?? 0,
    orderIndex: exam?.orderIndex ?? 1,
    isActive: exam?.isActive ?? true,
  });

  // Reset when target changes
  useEffect(() => {
    if (exam) {
      setForm({
        levelId: exam.levelId,
        type: exam.type,
        title: exam.title,
        description: exam.description ?? "",
        nLessons: exam.nLessons,
        orderIndex: exam.orderIndex,
        isActive: exam.isActive,
      });
    } else {
      setForm((f) => ({ ...f, levelId, title: "Mock exam", nLessons: 0, orderIndex: 1, isActive: true }));
    }
  }, [exam, levelId]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        levelId: form.levelId,
        type: form.type,
        title: form.title,
        description: form.description || null,
        nLessons: form.nLessons,
        orderIndex: form.orderIndex,
        isActive: form.isActive,
      };
      return exam
        ? (await api.patch(`/api/admin/exams/${exam.id}`, body)).data
        : (await api.post("/api/admin/exams", body)).data;
    },
    onSuccess: () => {
      FgToast.success(exam ? "Exam updated" : "Exam created");
      onSaved();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Save failed"),
  });

  return (
    <FgCard className="mt-6 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-[#FAFAFA]">
          {exam ? "Edit exam" : "New exam"}
        </h2>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-[#8E8E93] hover:text-[#FAFAFA] hover:bg-white/10 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
            Level
          </label>
          <select
            value={form.levelId}
            onChange={(e) => setForm({ ...form, levelId: e.target.value })}
            className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
          >
            {levels.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} · {l.displayName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
            Type
          </label>
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as ExamType })}
            className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
          >
            <option value="mock_exam">Mock exam</option>
            <option value="after_n_lessons">After N lessons</option>
          </select>
        </div>
        <FgInput
          label="Title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />
        <FgInput
          label="Order index"
          type="number"
          value={String(form.orderIndex)}
          onChange={(e) => setForm({ ...form, orderIndex: Number(e.target.value) || 1 })}
        />
        {form.type === "after_n_lessons" && (
          <FgInput
            label="N lessons (fire after this many)"
            type="number"
            value={String(form.nLessons)}
            onChange={(e) => setForm({ ...form, nLessons: Number(e.target.value) || 0 })}
          />
        )}
      </div>
      <div className="mt-3">
        <FgInput
          label="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div className="mt-3">
        <FgToggle
          label="Active (learners can attempt this exam)"
          checked={form.isActive}
          onChange={(v) => setForm({ ...form, isActive: v })}
        />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <FgButton variant="ghost" onClick={onClose}>
          Cancel
        </FgButton>
        <FgButton variant="primary" onClick={() => save.mutate()} loading={save.isPending}>
          Save
        </FgButton>
      </div>
    </FgCard>
  );
}
